import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AuthenticationHeader from '../../components/ui/AuthenticationHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import OTPInputField from './components/OTPInputField';
import CountdownTimer from './components/CountdownTimer';
import ResendCodeButton from './components/ResendCodeButton';
import VerificationStatus from './components/VerificationStatus';
import UserTypeSelector from './components/UserTypeSelector';

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from navigation state
  const email = location?.state?.email || 'user@example.com';

  // Component state
  const [otp, setOtp] = useState('');
  const [status, setStatus] = useState('idle'); // idle, verifying, success, error, expired
  const [error, setError] = useState('');
  const [showUserTypeSelector, setShowUserTypeSelector] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [isUserTypeLoading, setIsUserTypeLoading] = useState(false);

  // Mock valid OTP for testing
  const validOTP = '123456';
  const maxAttempts = 3;

  useEffect(() => {
    // Redirect if no email provided
    if (!email) {
      navigate('/email-authentication-entry');
    }
  }, [email, navigate]);

  const handleOTPChange = (value) => {
    console.log(
      'OTP Changed:',
      value,
      'Length:',
      value?.length,
      'Type:',
      typeof value
    );
    setOtp(value || '');
    setError('');
    setStatus('idle');
  };

  const handleVerifyOTP = async () => {
    const currentOTP = otp?.trim();
    console.log(
      'Verifying OTP:',
      currentOTP,
      'Valid OTP:',
      validOTP,
      'Match:',
      currentOTP === validOTP
    );

    if (!currentOTP || currentOTP?.length !== 6) {
      setError('لطفاً کد ۶ رقمی را کامل وارد کنید');
      return;
    }

    if (verificationAttempts >= maxAttempts) {
      setError('تعداد تلاش‌های مجاز به پایان رسیده است');
      setStatus('error');
      return;
    }

    setStatus('verifying');
    setError('');

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Use strict comparison and ensure both are strings
      if (String(currentOTP) === String(validOTP)) {
        console.log('OTP verification successful');
        setStatus('success');
        setTimeout(() => {
          setShowUserTypeSelector(true);
        }, 1500);
      } else {
        console.log('OTP verification failed - incorrect code');
        setVerificationAttempts((prev) => prev + 1);
        setStatus('error');
        const remainingAttempts = maxAttempts - verificationAttempts - 1;
        setError(
          `کد وارد شده صحیح نمی‌باشد. ${remainingAttempts} تلاش باقی‌مانده`
        );
        setOtp('');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setStatus('error');
      setError('خطا در اتصال به سرور. لطفاً مجدداً تلاش کنید');
    }
  };

  const handleTimerExpire = () => {
    setIsTimerActive(false);
    setIsResendDisabled(false);
    setStatus('expired');
  };

  const handleResendCode = async () => {
    console.log('Resending code');
    setIsTimerActive(true);
    setIsResendDisabled(true);
    setStatus('idle');
    setError('');
    setOtp('');
    setVerificationAttempts(0);
  };

  const handleUserTypeSelect = async (userType) => {
    setIsUserTypeLoading(true);

    try {
      // Simulate API call to save user type
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Navigate based on user type
      if (userType === 'lawyer') {
        navigate('/lawyer-profile-setup', {
          state: { email, userType, fromVerification: true },
        });
      } else {
        navigate('/legal-consultation-chatbot', {
          state: { email, userType, fromVerification: true },
        });
      }
    } catch (error) {
      console.error('Error selecting user type:', error);
      setIsUserTypeLoading(false);
    }
  };

  const handleBackToEmail = () => {
    navigate('/email-authentication-entry');
  };

  const isOTPComplete = otp?.length === 6;
  const isVerifyDisabled =
    !isOTPComplete ||
    status === 'verifying' ||
    verificationAttempts >= maxAttempts;

  return (
    <div className="min-h-screen bg-background">
      <AuthenticationHeader showHelp={true} />
      <main className="pt-16 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <AnimatePresence mode="wait">
            {!showUserTypeSelector ? (
              <motion.div
                key="verification"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-card rounded-lg shadow-moderate p-6 border border-border">
                {/* Back Button */}
                <div className="flex justify-end mb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToEmail}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="text-muted-foreground hover:text-foreground">
                    بازگشت
                  </Button>
                </div>

                {/* Status Display */}
                <div className="mb-6">
                  <VerificationStatus
                    status={status}
                    message={error}
                    email={email}
                  />
                </div>

                {/* OTP Input */}
                <div className="space-y-4 mb-6">
                  <div className="text-center">
                    <label className="block text-sm font-medium text-foreground mb-3">
                      کد تأیید ۶ رقمی را وارد کنید
                    </label>
                    <OTPInputField
                      length={6}
                      value={otp}
                      onChange={handleOTPChange}
                      disabled={
                        status === 'verifying' ||
                        verificationAttempts >= maxAttempts
                      }
                      error={status === 'error'}
                    />
                    {/* Debug info for testing */}
                    {/* {process.env?.NODE_ENV === 'development' && ( */}
                    <div className="mt-2 text-xs text-muted-foreground">
                      Current OTP: "{otp}" | Length: {otp?.length} | Valid:{' '}
                      {validOTP}
                    </div>
                    {/* )} */}
                  </div>

                  {/* Timer */}
                  {isTimerActive && (
                    <div className="text-center">
                      <CountdownTimer
                        initialTime={120}
                        onExpire={handleTimerExpire}
                        isActive={isTimerActive}
                      />
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-4">
                  {/* Verify Button */}
                  <Button
                    variant="default"
                    size="lg"
                    onClick={handleVerifyOTP}
                    disabled={isVerifyDisabled}
                    loading={status === 'verifying'}
                    fullWidth
                    iconName="Shield"
                    iconPosition="right">
                    {status === 'verifying' ? 'در حال تأیید...' : 'تأیید کد'}
                  </Button>

                  {/* Resend Code */}
                  <ResendCodeButton
                    onResend={handleResendCode}
                    disabled={isResendDisabled}
                    email={email}
                  />
                </div>

                {/* Attempts Warning */}
                {verificationAttempts > 0 &&
                  verificationAttempts < maxAttempts && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                      <div className="flex items-center text-sm text-warning">
                        <Icon name="AlertTriangle" size={16} className="ml-2" />
                        <span>
                          {maxAttempts - verificationAttempts} تلاش باقی‌مانده
                        </span>
                      </div>
                    </motion.div>
                  )}

                {/* Max Attempts Reached */}
                {verificationAttempts >= maxAttempts && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg">
                    <div className="flex items-center text-sm text-error">
                      <Icon name="XCircle" size={16} className="ml-2" />
                      <span>
                        تعداد تلاش‌های مجاز به پایان رسید. لطفاً کد جدید درخواست
                        کنید
                      </span>
                    </div>
                  </motion.div>
                )}

                {/* Help Text */}
                <div className="mt-6 p-3 bg-muted rounded-lg">
                  <div className="flex items-start space-x-reverse space-x-2">
                    <Icon
                      name="Info"
                      size={16}
                      className="text-muted-foreground mt-0.5 flex-shrink-0"
                    />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>• کد تأیید به مدت ۲ دقیقه معتبر است</p>
                      <p>• در صورت دریافت نکردن کد، پوشه اسپم را بررسی کنید</p>
                      <p>• کد تست: {validOTP}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="user-type-selector"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-card rounded-lg shadow-moderate p-6 border border-border">
                <UserTypeSelector
                  onSelect={handleUserTypeSelect}
                  isLoading={isUserTypeLoading}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default OTPVerification;
